import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { OnboardingCompanyProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import BooleanRadioGroup from '../../../../components/BooleanRadioGroup';
import Header from '../../../../components/Header';
import locales from './locales';

const validationSchema = z.object({
  hasActivityOutsideEU: z.boolean(),
});

type FormResult = z.infer<typeof validationSchema>;

interface CompanyActivityOutsideEUProps {
  isMandatee: boolean;
  isSaving: boolean;
  onSubmit: (values: FormResult) => void;
  companyProfile: Pick<
    OnboardingCompanyProfile,
    'hasActivityOutsideEU' | 'legalName'
  >;
}

const CompanyActivityOutsideEU = asBentoPage<CompanyActivityOutsideEUProps>(
  ({ companyProfile, isMandatee, isSaving, Layout, onPrev, onSubmit }) => {
    const {
      control,
      formState: { errors, isValid },
      handleSubmit,
    } = useForm<FormResult>({
      defaultValues: {
        hasActivityOutsideEU: companyProfile.hasActivityOutsideEU || undefined,
      },
      mode: 'onChange',
      resolver: zodResolver(validationSchema),
    });

    const getTitle = () => {
      if (companyProfile.legalName) {
        return locales.formatString(locales.title, {
          legalName: companyProfile.legalName,
        });
      }

      return locales.titleNoLegalName;
    };

    const getDescription = () => {
      if (isMandatee) {
        return locales.descriptionMandatee;
      }

      return locales.description;
    };

    return (
      <Layout
        data-testid="company-activity-outside-eu"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Header imageHeightPx={80} imageWidthPx={120} title={getTitle()} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.Field
            error={errors.hasActivityOutsideEU?.message}
            label={getDescription()}
          >
            <Controller
              control={control}
              name="hasActivityOutsideEU"
              render={({ field }) => (
                <BooleanRadioGroup
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </Form.Field>
          <Button
            isDisabled={!isValid}
            isExpanded
            isLoading={isSaving}
            marginTop="space-40"
            type="submit"
          >
            {locales.cta}
          </Button>
        </form>
      </Layout>
    );
  },
);

export default CompanyActivityOutsideEU;
